import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Send, Calendar, DollarSign } from 'lucide-react';

interface SpecialRequest {
  id: string;
  email?: string;
  full_name?: string | null;
  phone?: string | null;
  materials_needed: string;
  quantities: string | null;
  budget: number | null;
  deadline: string | null;
  message: string | null;
  status: string;
  admin_response: string | null;
  created_at: string;
  user_id: string | null;
}

interface SpecialRequestDetailModalProps {
  request: SpecialRequest | null;
  isOpen: boolean;
  onClose: () => void;
  onRequestUpdated: () => void;
}

const SpecialRequestDetailModal = ({
  request,
  isOpen,
  onClose,
  onRequestUpdated
}: SpecialRequestDetailModalProps) => {
  const [adminResponse, setAdminResponse] = useState('');
  const [isResponding, setIsResponding] = useState(false);
  const [loading, setLoading] = useState(false);

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'ממתין';
      case 'responded':
        return 'נענה';
      case 'completed':
        return 'הושלם';
      case 'rejected':
        return 'נדחה';
      default:
        return status;
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'pending':
        return 'secondary';
      case 'responded':
        return 'default';
      case 'completed':
        return 'default';
      case 'rejected':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const isUrgent = (deadline: string | null) => {
    if (!deadline) return false;
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    return deadlineDate <= weekFromNow;
  };

  const handleSendResponse = async () => {
    if (!request || !adminResponse.trim()) {
      toast.error('אנא הזן תגובה');
      return;
    }

    setLoading(true);
    try {
      // First update the database
      const { error } = await supabase
        .from('special_search_requests')
        .update({
          status: 'responded',
          admin_response: adminResponse.trim(),
        })
        .eq('id', request.id);

      if (error) throw error;

      // Then send email via edge function
      const { data: emailData, error: emailError } = await supabase.functions.invoke('send-special-search-response', {
        body: {
          requestId: request.id,
          responseMessage: adminResponse.trim(),
          adminName: 'צוות Excess Market'
        }
      });

      if (emailError) {
        console.error('Error sending email:', emailError);
        toast.error('הבקשה עודכנה אבל המייל לא נשלח');
      } else {
        toast.success('תגובה נשלחה בהצלחה למייל הלקוח');
      }

      setAdminResponse('');
      setIsResponding(false);
      onRequestUpdated();
      onClose();
    } catch (error: any) {
      console.error('Error sending response:', error);
      toast.error(`שגיאה בשליחת התגובה: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!request) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" dir="rtl">
        <DialogHeader>
          <DialogTitle>פרטי בקשת 'מצא לי מוצר'</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Request Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">שם מלא</Label>
              <p className="text-sm">{request?.full_name || 'לא צוין'}</p>
            </div>

            <div>
              <Label className="text-sm font-medium">אימייל</Label>
              <p className="text-sm">{request?.email || 'לא זמין'}</p>
            </div>

            {request?.phone && (
              <div>
                <Label className="text-sm font-medium">טלפון</Label>
                <p className="text-sm">{request.phone}</p>
              </div>
            )}

            <div>
              <Label className="text-sm font-medium">סטטוס</Label>
              <Badge variant={getStatusBadgeVariant(request.status)} className="w-fit">
                {getStatusText(request.status)}
              </Badge>
            </div>

            <div>
              <Label className="text-sm font-medium">תאריך יצירה</Label>
              <p className="text-sm">{new Date(request.created_at).toLocaleDateString('he-IL')}</p>
            </div>
          </div>

          {/* Materials and Budget */}
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">חומרים נדרשים</Label>
              <div className="mt-1 p-3 bg-muted rounded-md">
                <p className="text-sm whitespace-pre-wrap">{request.materials_needed}</p>
              </div>
            </div>

            {request.quantities && (
              <div>
                <Label className="text-sm font-medium">כמויות</Label>
                <div className="mt-1 p-3 bg-muted rounded-md">
                  <p className="text-sm whitespace-pre-wrap">{request.quantities}</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {request.budget && (
                <div>
                  <Label className="text-sm font-medium flex items-center gap-1">
                    <DollarSign className="w-4 h-4" />
                    תקציב
                  </Label>
                  <p className="text-sm font-medium">₪{request.budget.toLocaleString()}</p>
                </div>
              )}

              {request.deadline && (
                <div>
                  <Label className="text-sm font-medium flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    מועד יעד
                  </Label>
                  <p className={`text-sm font-medium ${isUrgent(request.deadline) ? 'text-red-600' : 'text-foreground'
                    }`}>
                    {new Date(request.deadline).toLocaleDateString('he-IL')}
                    {isUrgent(request.deadline) && ' (דחוף!)'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Customer Message */}
          {request.message && (
            <div>
              <Label className="text-sm font-medium">הודעת הלקוח</Label>
              <div className="mt-1 p-3 bg-muted rounded-md">
                <p className="text-sm whitespace-pre-wrap">{request.message}</p>
              </div>
            </div>
          )}

          {/* Previous Admin Response */}
          {request.admin_response && (
            <div>
              <Label className="text-sm font-medium">תגובת המנהל</Label>
              <div className="mt-1 p-3 bg-primary/10 rounded-md">
                <p className="text-sm whitespace-pre-wrap">{request.admin_response}</p>
              </div>
            </div>
          )}

          {/* Response Section */}
          {request.status === 'pending' && (
            <div className="space-y-4">
              {!isResponding ? (
                <Button onClick={() => setIsResponding(true)} className="w-full">
                  <Send className="w-4 h-4 mr-2" />
                  מענה לבקשה
                </Button>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="admin-response">תגובת המנהל</Label>
                    <Textarea
                      id="admin-response"
                      value={adminResponse}
                      onChange={(e) => setAdminResponse(e.target.value)}
                      placeholder="הזן את תגובתך לבקשה המיוחדת..."
                      rows={4}
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={handleSendResponse} disabled={loading} className="flex-1">
                      {loading ? 'שולח...' : 'שלח תגובה'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsResponding(false);
                        setAdminResponse('');
                      }}
                      className="flex-1"
                    >
                      ביטול
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SpecialRequestDetailModal;